import { useCallback, useEffect, useRef, useState } from 'react';

import { getPblCaseMatchingStatus } from '@/api/pbl.api';
import type { PblMatchingExperimentStatus, PblMatchingStatusResponse } from '@/types/pbl';

const MAX_AUTOMATIC_POLLING_MS = 15 * 60 * 1000;
const TRANSIENT_BACKOFF_MS = [2000, 4000, 8000, 15000];
const terminalCaseStatuses = new Set([
  'ai_matched',
  'manual_assignment_required',
  'case_cancelled',
  'lawyer_invited',
  'lawyer_accepted',
  'closed',
]);

export const isPblMatchingProcessing = (
  caseStatus?: string | null,
  matchingStatus?: string | null
) =>
  caseStatus === 'ai_matching_in_progress' || ['queued', 'running'].includes(matchingStatus || '');

export const getPblMatchingPollDelay = (serverDelayMs: number | undefined, elapsedMs: number) => {
  const safeServerDelay = Math.min(15000, Math.max(1000, serverDelayMs || 2000));
  if (elapsedMs < 60_000) return safeServerDelay;
  if (elapsedMs < 5 * 60_000) return Math.max(5000, safeServerDelay);
  return Math.max(15000, safeServerDelay);
};

type Options = {
  caseId?: number;
  enabled: boolean;
  initialCaseStatus?: string | null;
  initialMatching?: PblMatchingExperimentStatus | null;
  onTerminal: (status: PblMatchingStatusResponse) => Promise<void> | void;
};

export const usePblMatchingStatusPolling = ({
  caseId,
  enabled,
  initialCaseStatus,
  initialMatching,
  onTerminal,
}: Options) => {
  const [status, setStatus] = useState<PblMatchingStatusResponse | null>(() => ({
    case_status: initialCaseStatus as any,
    matching: initialMatching || null,
    results_ready: false,
    manual_selection_available: false,
  }));
  const [elapsedMs, setElapsedMs] = useState(0);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [autoStopped, setAutoStopped] = useState(false);
  const pollNowRef = useRef<(() => void) | null>(null);
  const onTerminalRef = useRef(onTerminal);

  useEffect(() => {
    onTerminalRef.current = onTerminal;
  }, [onTerminal]);

  useEffect(() => {
    setStatus((current) => ({
      case_status: (initialCaseStatus || current?.case_status) as any,
      matching: initialMatching ?? current?.matching ?? null,
      results_ready: current?.results_ready ?? false,
      manual_selection_available: current?.manual_selection_available ?? false,
    }));
  }, [initialCaseStatus, initialMatching]);

  useEffect(() => {
    if (!enabled || !caseId) return undefined;

    let active = true;
    let inFlight = false;
    let timerId: number | undefined;
    let controller: AbortController | undefined;
    let terminalHandled = false;
    let failureCount = 0;
    const parsedStartedAt = initialMatching?.started_at ? Date.parse(initialMatching.started_at) : Number.NaN;
    const pollingStartedAt = Number.isFinite(parsedStartedAt) ? parsedStartedAt : Date.now();
    setAutoStopped(false);
    setConsecutiveFailures(0);

    const clearTimer = () => {
      if (timerId !== undefined) window.clearTimeout(timerId);
      timerId = undefined;
    };

    const schedule = (delayMs: number, poll: (manual?: boolean) => Promise<void>) => {
      clearTimer();
      timerId = window.setTimeout(() => void poll(false), delayMs);
    };

    const poll = async (manual = false): Promise<void> => {
      if (!active || inFlight || (!manual && document.hidden)) return;

      const elapsed = Math.max(0, Date.now() - pollingStartedAt);
      setElapsedMs(elapsed);
      inFlight = true;
      controller = new AbortController();
      try {
        const response = await getPblCaseMatchingStatus(caseId, controller.signal);
        if (!active) return;
        if (response.data.success === false) throw new Error('matching_status_refresh_failed');

        const next = response.data.data;
        failureCount = 0;
        setConsecutiveFailures(0);
        setStatus(next);

        const processing = isPblMatchingProcessing(next.case_status, next.matching?.status);
        const terminal = !processing && (
          next.matching?.status === 'completed' ||
          next.matching?.status === 'failed' ||
          terminalCaseStatuses.has(next.case_status)
        );

        if (terminal) {
          setAutoStopped(false);
          if (!terminalHandled && !document.hidden) {
            await onTerminalRef.current(next);
            terminalHandled = true;
          }
          return;
        }

        // 先取得最新結果再決定暫停，避免舊紀錄或最後一次間隔錯過完成狀態。
        if (elapsed >= MAX_AUTOMATIC_POLLING_MS) {
          setAutoStopped(true);
          return;
        }
        schedule(getPblMatchingPollDelay(next.matching?.poll_after_ms, elapsed), poll);
      } catch (error: any) {
        if (!active || error?.code === 'ERR_CANCELED') return;
        const httpStatus = error?.response?.status;
        if ([401, 403, 404].includes(httpStatus)) return;

        failureCount += 1;
        setConsecutiveFailures(failureCount);
        if (elapsed >= MAX_AUTOMATIC_POLLING_MS) {
          setAutoStopped(true);
          return;
        }
        const backoff = TRANSIENT_BACKOFF_MS[Math.min(failureCount - 1, TRANSIENT_BACKOFF_MS.length - 1)];
        schedule(backoff, poll);
      } finally {
        inFlight = false;
      }
    };

    pollNowRef.current = () => void poll(true);

    const resume = () => {
      if (!document.hidden) void poll(true);
    };
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('focus', resume);
    schedule(0, poll);

    return () => {
      active = false;
      pollNowRef.current = null;
      clearTimer();
      controller?.abort();
      document.removeEventListener('visibilitychange', resume);
      window.removeEventListener('focus', resume);
    };
  }, [caseId, enabled, initialMatching?.started_at]);

  const checkNow = useCallback(() => pollNowRef.current?.(), []);

  return {
    status,
    elapsedMs,
    consecutiveFailures,
    autoStopped,
    checkNow,
  };
};
